import { MediaImage } from "@/components/admin/MediaAsset";
import { useEffect, useRef, useState } from "react";
import { Lightbulb, Sparkles, Loader2, Bookmark, BookmarkCheck, Trash2, RefreshCw, Send, CalendarClock, Images } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MediaGallery from "@/components/admin/MediaGallery";

import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${path}`;

const CATEGORY_STYLE = {
  Edukasi: "bg-[#10B981]/15 text-[#059669]",
  Promosi: "bg-[#F59E0B]/15 text-[#B45309]",
  Branding: "bg-[#3B82F6]/15 text-[#3B82F6]",
  Testimoni: "bg-[#A855F7]/15 text-[#A855F7]",
  Trending: "bg-[#F43F5E]/15 text-[#E11D48]",
};

const PLATFORM_LABEL = { instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook", blog: "Blog" };
const VALID_CHANNELS = ["instagram", "tiktok", "facebook", "blog"];

const toBrief = (idea) => ({
  text: `${idea.title}\n\n${idea.description}${idea.category ? `\n\nKategori: ${idea.category}` : ""}${idea.platform ? ` · Platform: ${PLATFORM_LABEL[idea.platform] || idea.platform}` : ""}`,
  channel: VALID_CHANNELS.includes(idea.platform) ? idea.platform : "",
});

export default function ContentIdeas(props) {
  return <BusinessContentIdeas key={props.activeBusiness?.id || "no-business"} {...props} />;
}

function BusinessContentIdeas({ activeBusiness, onUseIdea, role }) {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIdea, setCarouselIdea] = useState(null);
  const [carouselStyle, setCarouselStyle] = useState("storytelling");
  const [carouselCount, setCarouselCount] = useState(4);
  const [carouselBusy, setCarouselBusy] = useState(false);
  const [carouselSlides, setCarouselSlides] = useState(null);
  const [carouselMediaSlide, setCarouselMediaSlide] = useState(null);
  const carouselRequest = useRef(0);
  const [ideas, setIdeas] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingTitle, setSavingTitle] = useState("");
  const [seriesOpen, setSeriesOpen] = useState(false);
  const [seriesTime, setSeriesTime] = useState("09:00");
  const [seriesDate, setSeriesDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [schedulingSeries, setSchedulingSeries] = useState(false);

  const scheduleSeries = async () => {
    if (!activeBusiness?.id) { toast.error("Pilih bisnis dulu"); return; }
    if (!saved.length) { toast.error("Belum ada ide tersimpan"); return; }
    setSchedulingSeries(true);
    try {
      const r = await api.post("/content-ideas/schedule-series", {
        business_id: activeBusiness.id,
        idea_ids: saved.map((s) => s.id),
        time: seriesTime,
        start_date: seriesDate,
        reminder_minutes: 15,
      });
      toast.success(`${r.data.created} jadwal seri dibuat — cek di menu Riwayat & Jadwal`);
      setSeriesOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSchedulingSeries(false);
    }
  };

  const loadSaved = async () => {
    try {
      const response = await api.get("/content-ideas/saved");
      if (mounted.current) setSaved(response.data || []);
    } catch { /* abaikan */ }
  };
  useEffect(() => { loadSaved(); }, []);

  const generate = async () => {
    if (!activeBusiness?.id || loading) return;
    setLoading(true);
    setIdeas([]);
    try {
      const r = await api.post("/content-ideas/generate", { business_id: activeBusiness.id });
      if (!mounted.current) return;
      setIdeas(r.data || []);
      toast.success("10 ide konten baru dibuat");
    } catch (e) {
      if (mounted.current) toast.error(apiError(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const isSaved = (idea) => saved.some((s) => s.title === idea.title);

  const save = async (idea) => {
    setSavingTitle(idea.title);
    try {
      const r = await api.post("/content-ideas/saved", idea);
      setSaved((s) => [r.data, ...s]);
      toast.success("Ide disimpan ke favorit");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingTitle("");
    }
  };

  const removeSaved = async (id) => {
    try {
      await api.delete(`/content-ideas/saved/${id}`);
      setSaved((s) => s.filter((x) => x.id !== id));
      toast.success("Ide dihapus dari favorit");
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendIdea = (idea) => {
    onUseIdea?.(toBrief(idea));
    toast.success(`Ide "${idea.title}" dikirim ke Generate Konten`);
  };

  const openCarousel = (idea) => {
    carouselRequest.current += 1;
    setCarouselBusy(false);
    setCarouselMediaSlide(null);
    setCarouselIdea(idea);
    setCarouselSlides(null);
    setCarouselOpen(true);
  };

  const changeCarouselOpen = (open) => {
    setCarouselOpen(open);
    if (!open) {
      carouselRequest.current += 1;
      setCarouselBusy(false);
      setCarouselMediaSlide(null);
    }
  };

  const generateCarouselDraft = async () => {
    if (!carouselIdea || !activeBusiness?.id || carouselBusy) return;
    const request = ++carouselRequest.current;
    setCarouselBusy(true);
    try {
      const previousCount = carouselSlides?.captions.length || 0;
      const previousImages = carouselSlides?.images || [];
      let defaults = [];
      if (carouselCount > previousCount) {
        const media = (await api.get("/media")).data || [];
        defaults = [...new Set(media.map((m) => fileUrl(m.storage_path)))].filter((url) => !previousImages.includes(url));
      }
      if (!mounted.current || request !== carouselRequest.current) return;
      const images = Array.from({ length: carouselCount }, (_, i) => i < previousCount ? previousImages[i] || "" : defaults.shift() || "");
      const r = await api.post("/content/slide-captions", {
        business_id: activeBusiness?.id || null,
        context: toBrief(carouselIdea).text.slice(0, 800),
        count: carouselCount,
        style: carouselStyle,
      });
      if (!mounted.current || request !== carouselRequest.current) return;
      setCarouselSlides({ images, captions: r.data.captions || [] });
      toast.success("Draf carousel dibuat");
    } catch (e) {
      if (mounted.current && request === carouselRequest.current) toast.error(apiError(e));
    } finally {
      if (mounted.current && request === carouselRequest.current) setCarouselBusy(false);
    }
  };

  const updateCarouselImage = (url) => {
    if (carouselMediaSlide === null || !carouselSlides || carouselBusy) return;
    if (carouselSlides.images.some((image, i) => image === url && i !== carouselMediaSlide)) {
      toast.error("Gambar ini sudah dipakai pada slide lain. Pilih gambar yang berbeda.");
      return;
    }
    setCarouselSlides((previous) => ({
      ...previous,
      images: Array.from({ length: previous.captions.length }, (_, i) => i === carouselMediaSlide ? url : previous.images[i] || ""),
    }));
    setCarouselMediaSlide(null);
  };

  const updateCarouselCaption = (i, val) =>
    setCarouselSlides((s) => ({ ...s, captions: s.captions.map((c, idx) => (idx === i ? val : c)) }));

  const useCarouselInGenerator = () => {
    if (!carouselSlides) return;
    const slides = carouselSlides.captions.map((caption, i) => ({ image: carouselSlides.images[i], caption })).filter((slide) => slide.image);
    onUseIdea?.({ ...toBrief(carouselIdea), carousel: { images: slides.map((slide) => slide.image), captions: slides.map((slide) => slide.caption) } });
    changeCarouselOpen(false);
    toast.success("Draf carousel dikirim ke Generate Konten — klik Generate lalu posting");
  };

  const badge = (idea) => (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {idea.category && (
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${CATEGORY_STYLE[idea.category] || "bg-[#F1F5F9] text-slate-600"}`}>{idea.category}</span>
      )}
      {idea.platform && (
        <span className="text-[11px] font-mono-code px-2 py-0.5 rounded bg-[#F1F5F9] text-slate-600">{PLATFORM_LABEL[idea.platform] || idea.platform}</span>
      )}

    </div>
  );

  return (
    <div className="space-y-8">
      {/* Generator */}
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Lightbulb size={18} className="text-[#B45309]" />
              <h2 className="font-heading font-bold">Ide Konten Viral</h2>
            </div>
            <p className="text-xs text-slate-500">
              10 ide konten relevan untuk {activeBusiness?.name || "bisnis Anda"} — disesuaikan AI dengan tren &amp; momen bulan berjalan.
            </p>
          </div>
          <Button onClick={generate} disabled={loading || !activeBusiness?.id} data-testid={ideas.length ? "idea-refresh-button" : "idea-generate-button"} className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
            {loading ? <Loader2 size={16} className="animate-spin mr-1" /> : ideas.length ? <RefreshCw size={16} className="mr-1" /> : <Sparkles size={16} className="mr-1" />}
            {loading ? "Menyusun ide..." : ideas.length ? "Generate 10 Ide Baru" : "Generate 10 Ide"}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-6 sm:p-12 text-center">
          <Loader2 size={32} className="text-[#2563EB] animate-spin mx-auto mb-3" />
          <p className="text-slate-600 text-sm">AI sedang menyusun 10 ide konten sesuai momen bulan ini...</p>
        </div>
      )}

      {!loading && ideas.length === 0 && (
        <div className="bg-[#FFFFFF] border border-dashed border-[#E2E8F0] rounded-2xl p-6 sm:p-12 text-center" data-testid="idea-empty-state">
          <Lightbulb size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-600">{activeBusiness?.id ? 'Klik "Generate 10 Ide" untuk mendapatkan ide konten baru sesuai bisnis Anda.' : "Pilih bisnis terlebih dahulu untuk membuat ide konten."}</p>
        </div>
      )}

      {!loading && ideas.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="idea-list">
          {ideas.map((idea, i) => (
            <div key={`${idea.title}-${i}`} data-testid={`idea-card-${i}`} className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-5 hover:border-[#3B82F6] transition-colors flex flex-col">
              {badge(idea)}
              <h4 className="font-heading font-semibold text-sm mb-1.5">{idea.title}</h4>
              <p className="text-xs text-slate-600 leading-relaxed flex-1">{idea.description}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button
                  onClick={() => save(idea)}
                  disabled={isSaved(idea) || savingTitle === idea.title}
                  variant="outline"
                  size="sm"
                  data-testid={`idea-save-${i}`}
                  className="flex-1 border-[#F59E0B]/40 bg-[#F59E0B]/10 hover:bg-[#F59E0B]/25 text-[#B45309] rounded-xl text-xs disabled:opacity-60"
                >
                  {savingTitle === idea.title ? <Loader2 size={13} className="animate-spin mr-1" /> : isSaved(idea) ? <BookmarkCheck size={13} className="mr-1" /> : <Bookmark size={13} className="mr-1" />}
                  {isSaved(idea) ? "Tersimpan" : "Simpan"}
                </Button>
                <Button onClick={() => openCarousel(idea)} size="sm" variant="outline" data-testid={`idea-carousel-${i}`} className="border-purple-500/40 text-purple-700 text-xs"><Images size={14} className="mr-1" />Draf Carousel</Button>
                <Button onClick={() => sendIdea(idea)} size="sm" data-testid={`idea-create-${i}`} className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl text-xs">
                  <Send size={13} className="mr-1" /> Buat Konten
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Favorit */}
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6" data-testid="saved-ideas-card">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <BookmarkCheck size={18} className="text-[#B45309]" />
          <h2 className="font-heading font-bold">Ide Tersimpan (Favorit)</h2>
          <span className="text-xs text-slate-500">({saved.length})</span>
          <div className="flex-1" />
          {role !== "operator" && saved.length > 0 && (
            <Button onClick={() => setSeriesOpen(true)} size="sm" data-testid="idea-series-open-button" className="bg-[#10B981] hover:bg-[#0DA271] rounded-xl text-xs">
              <CalendarClock size={14} className="mr-1" /> Jadwalkan Seri Mingguan
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-4">Kumpulan ide menarik yang Anda simpan untuk digarap nanti — atau jadwalkan semuanya sekaligus: 1 ide per hari kerja (Senin–Jumat) dikirim via WhatsApp.</p>
        {saved.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="saved-ideas-empty">Belum ada ide tersimpan. Generate ide lalu klik "Simpan".</p>
        ) : (
          <div className="space-y-2">
            {saved.map((s) => (
              <div key={s.id} data-testid={`saved-idea-${s.id}`} className="flex flex-wrap items-center gap-3 bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-3">
                <div className="w-full min-w-0 flex-1 sm:min-w-[200px]">
                  {badge(s)}
                  <p className="text-sm font-medium text-slate-800">{s.title}</p>
                  {s.description && <p className="text-xs text-slate-600 mt-0.5">{s.description}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button onClick={() => openCarousel(s)} size="sm" variant="outline" data-testid={`saved-idea-carousel-${s.id}`} className="border-purple-500/40 text-purple-700 text-xs"><Images size={14} className="mr-1" />Draf Carousel</Button>
                <Button onClick={() => sendIdea(s)} size="sm" data-testid={`saved-idea-create-${s.id}`} className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl text-xs">
                    <Send size={13} className="mr-1" /> Buat Konten
                  </Button>
                  <button onClick={() => removeSaved(s.id)} data-testid={`saved-idea-delete-${s.id}`} className="p-2 rounded-lg hover:bg-[#F43F5E]/20 text-slate-600 hover:text-[#E11D48] transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={seriesOpen} onOpenChange={setSeriesOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-sm" data-testid="idea-series-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><CalendarClock size={18} className="text-[#059669]" /> Seri Mingguan</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              {saved.length} ide favorit akan dijadwalkan 1 per hari kerja (Senin–Jumat) dan dikirim sebagai pengingat WhatsApp ke nomor admin bisnis {activeBusiness?.name || ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Mulai tanggal</Label>
              <Input type="date" value={seriesDate} onChange={(e) => setSeriesDate(e.target.value)} data-testid="idea-series-date-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Jam kirim (WIB)</Label>
              <Input type="time" value={seriesTime} onChange={(e) => setSeriesTime(e.target.value)} data-testid="idea-series-time-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" />
            </div>
            <p className="text-[11px] text-slate-500">Pastikan nomor WhatsApp admin &amp; token Fonnte sudah diatur di menu Settings.</p>
            <Button onClick={scheduleSeries} disabled={schedulingSeries} data-testid="idea-series-submit-button" className="w-full bg-[#10B981] hover:bg-[#0DA271] rounded-xl">
              {schedulingSeries ? <Loader2 size={16} className="animate-spin mr-1" /> : <CalendarClock size={16} className="mr-1" />}
              Jadwalkan {saved.length} Ide
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={carouselOpen} onOpenChange={changeCarouselOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-2xl max-h-[min(85vh,calc(100dvh-2rem))] overflow-y-auto" data-testid="idea-carousel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Images size={18} className="text-[#7C3AED]" /> Draf Carousel</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              {carouselIdea?.title} — AI menulis caption per slide sesuai gaya. Gambar awal dari galeri bisa diganti pada masing-masing slide.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <Label className="text-xs text-slate-600">Gaya storytelling</Label>
              <Select value={carouselStyle} onValueChange={setCarouselStyle} disabled={carouselBusy}>
                <SelectTrigger data-testid="idea-carousel-style" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1 w-[160px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                  <SelectItem value="storytelling" data-testid="idea-carousel-style-storytelling">Storytelling</SelectItem>
                  <SelectItem value="edukasi" data-testid="idea-carousel-style-edukasi">Edukasi</SelectItem>
                  <SelectItem value="testimoni" data-testid="idea-carousel-style-testimoni">Testimoni</SelectItem>
                  <SelectItem value="bts" data-testid="idea-carousel-style-bts">Behind The Scene</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Jumlah slide</Label>
              <Select value={String(carouselCount)} onValueChange={(v) => setCarouselCount(parseInt(v, 10))} disabled={carouselBusy}>
                <SelectTrigger data-testid="idea-carousel-count" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1 w-[90px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                  {[2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generateCarouselDraft} disabled={carouselBusy} data-testid="idea-carousel-generate" className="bg-[#7C3AED] hover:bg-[#9333EA] rounded-xl text-xs">
              {carouselBusy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
              {carouselSlides ? "Regenerate" : "Generate Draf"}
            </Button>
          </div>
          {carouselSlides && (
            <>
              {carouselSlides.images.filter(Boolean).length === 0 && (
                <p className="text-[11px] text-[#D97706] mb-2" data-testid="idea-carousel-no-images">Draf belum memiliki gambar. Klik Pilih Gambar pada slide untuk memilih atau mengunggah media.</p>
              )}
              {carouselSlides.images.filter(Boolean).length < carouselSlides.captions.length && carouselSlides.images.some(Boolean) && (
                <p className="text-[11px] text-[#D97706] mb-2" data-testid="idea-carousel-partial-images">Sebagian slide belum memiliki gambar. Klik Pilih Gambar pada slide tersebut untuk melengkapinya.</p>
              )}
              <div className="grid grid-cols-2 gap-3 mb-4" data-testid="idea-carousel-slides">
                {carouselSlides.captions.map((cap, i) => (
                  <div key={i} className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-2" data-testid={`idea-carousel-slide-${i}`}>
                    {carouselSlides.images[i] ? (
                      <div className="relative h-24 rounded-lg overflow-hidden mb-2">
                        <MediaImage src={carouselSlides.images[i]} alt={`slide ${i + 1}`} className="w-full h-full object-cover" />
                        <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-[#7C3AED] text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                      </div>
                    ) : (
                      <div className="h-24 rounded-lg bg-[#FFFFFF] border border-dashed border-[#E2E8F0] flex items-center justify-center mb-2 text-slate-600 text-[10px]">Slide {i + 1} · tanpa gambar</div>
                    )}
                    <Button type="button" variant="outline" size="sm" onClick={() => setCarouselMediaSlide(i)} disabled={carouselBusy} aria-label={`${carouselSlides.images[i] ? "Ganti" : "Pilih"} gambar slide ${i + 1}`} data-testid={`idea-carousel-image-${i}`} className="w-full h-auto min-h-9 whitespace-normal mb-2 border-[#E2E8F0] bg-[#FFFFFF] hover:bg-[#E2E8F0] text-xs"><Images size={13} className="mr-1 shrink-0" />{carouselSlides.images[i] ? "Ganti Gambar" : "Pilih Gambar"}</Button>
                    <Textarea value={cap} rows={3} maxLength={300} disabled={carouselBusy} onChange={(e) => updateCarouselCaption(i, e.target.value)} data-testid={`idea-carousel-caption-${i}`} className="bg-[#FFFFFF] border-[#E2E8F0] text-[11px] resize-none" />
                  </div>
                ))}
              </div>
              <Button onClick={useCarouselInGenerator} disabled={carouselBusy} data-testid="idea-carousel-use" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
                <Send size={14} className="mr-1" /> Buka di Generate Konten
              </Button>
            </>
          )}
        </DialogContent>
        <Dialog open={carouselMediaSlide !== null} onOpenChange={(open) => { if (!open) setCarouselMediaSlide(null); }}>
          <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-3xl max-h-[min(85vh,calc(100dvh-2rem))] overflow-y-auto" data-testid="idea-carousel-media-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Pilih Gambar untuk Slide {(carouselMediaSlide ?? 0) + 1}</DialogTitle>
              <DialogDescription className="text-slate-500 text-xs">Pilih atau unggah gambar dari Galeri Media. Caption slide tetap tersimpan.</DialogDescription>
            </DialogHeader>
            <MediaGallery key={carouselMediaSlide} picker onPick={updateCarouselImage} />
          </DialogContent>
        </Dialog>
      </Dialog>
    </div>
  );
}
